import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Empty, IncidentBadge, PriorityPill, SlaBadge } from '../components/ui';
import { useDebounced, useNow, useRealtime, useResource } from '../state/hooks';
import { dateTime, elapsed } from '../lib/format';
import type { Category, Incident, Priority } from '../lib/types';

/**
 * The incident register: one searchable list of everything, open or closed.
 *
 * The search box deliberately spans case number, caller, address and narrative
 * at once. Somebody checking whether a call is a duplicate has a fragment of
 * something — a street, half a phone number — and should not have to guess
 * which field it belongs to first.
 */
export function IncidentsPage() {
  const now = useNow(5000);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'open' | 'all' | 'closed'>('open');
  const [categoryId, setCategoryId] = useState('');
  const [priorityId, setPriorityId] = useState('');

  const debouncedSearch = useDebounced(search, 300);
  const taxonomy = useResource<{ categories: Category[]; priorities: Priority[] }>('/admin/taxonomy');

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '200' });
    if (status === 'open') params.set('open', 'true');
    if (status === 'closed') params.set('status', 'closed,cancelled,resolved');
    if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
    if (categoryId) params.set('categoryId', categoryId);
    if (priorityId) params.set('priorityId', priorityId);
    return `/incidents?${params.toString()}`;
  }, [status, debouncedSearch, categoryId, priorityId]);

  const incidents = useResource<{ incidents: Incident[] }>(path);
  useRealtime(['incidents'], () => incidents.reload(), () => incidents.reload());

  const rows = incidents.data?.incidents ?? [];

  return (
    <div className="stack">
      <Card>
        <div className="row">
          <input
            type="text"
            placeholder="Search case number, caller, address or narrative…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 240 }}
          />
          <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} style={{ width: 'auto' }}>
            <option value="open">Open</option>
            <option value="closed">Resolved &amp; closed</option>
            <option value="all">All</option>
          </select>
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} style={{ width: 'auto' }}>
            <option value="">All types</option>
            {(taxonomy.data?.categories ?? []).map((category) => (
              <option key={category.id} value={category.id}>{category.name}</option>
            ))}
          </select>
          <select value={priorityId} onChange={(e) => setPriorityId(e.target.value)} style={{ width: 'auto' }}>
            <option value="">All priorities</option>
            {(taxonomy.data?.priorities ?? []).map((priority) => (
              <option key={priority.id} value={priority.id}>{priority.code}</option>
            ))}
          </select>
        </div>
      </Card>

      <Card title={`Incidents (${rows.length})`} flush>
        {incidents.loading && !incidents.data ? (
          <Empty>Loading…</Empty>
        ) : rows.length === 0 ? (
          <Empty>No incidents match those filters.</Empty>
        ) : (
          <div className="scroll" style={{ maxHeight: 'calc(100vh - 260px)' }}>
            <table>
              <thead>
                <tr>
                  <th>Case</th>
                  <th>Type</th>
                  <th>Location</th>
                  <th>Caller</th>
                  <th>Station</th>
                  <th>Units</th>
                  <th>Status</th>
                  <th>Opened</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((incident) => (
                  <tr key={incident.id}>
                    <td>
                      <div className="row tight">
                        <PriorityPill
                          code={incident.priority_code}
                          color={incident.priority_color}
                          level={incident.priority_level}
                        />
                        <Link to={`/incidents/${incident.id}`} className="mono">{incident.incident_no}</Link>
                      </div>
                      <SlaBadge
                        dueAt={incident.sla_due_at}
                        breachedAt={incident.sla_breached_at}
                        dispatchedAt={incident.dispatched_at}
                        now={now}
                      />
                    </td>
                    <td>{incident.category_name ?? '—'}</td>
                    <td className="truncate" style={{ maxWidth: 260 }}>
                      {incident.location_address ?? '—'}
                      {incident.location_details && <div className="faint truncate">{incident.location_details}</div>}
                    </td>
                    <td>
                      {incident.caller_name ?? '—'}
                      <div className="faint">{incident.caller_phone ?? ''}</div>
                    </td>
                    <td>
                      {incident.station_name
                        ? <span className="badge accent">{incident.station_code}</span>
                        : <span className="faint">—</span>}
                      {incident.station_name && (
                        <div className="faint truncate" style={{ maxWidth: 130 }}>{incident.station_name}</div>
                      )}
                    </td>
                    <td>
                      {incident.units.length === 0
                        ? <span className="faint">—</span>
                        : incident.units.map((unit) => unit.callSign).join(', ')}
                    </td>
                    <td><IncidentBadge status={incident.status} /></td>
                    <td>
                      {dateTime(incident.created_at)}
                      <div className="faint">{elapsed(incident.created_at, now)} ago</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
